import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import routes from 'routes';
import { resultsToEventTrackTableData } from 'services/admin/ResultService';
import { getClubRecordsWithAgeGroups, getMeetSummary } from 'api/clubApi';
import MetaTags from 'components/common/MetaTags';
import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import EventResultPageHeader from 'components/admin/result/EventResultPageHeader';
import EventResultPageOptionButton from 'components/admin/result/EventResultPageOptionButton';
import ResultDataTable from 'components/admin/result/ResultDataTable';
import ResultEditPopup from 'components/admin/ResultEditPopup';
import { convertDateTime } from 'utils/time';
import { getUserPermissionLevel } from 'services/auth/tokenService';
import { UserPermissionLevel } from 'constants/auth';
import EventPopup from 'components/admin/EventPopup';
import { useEventRoundData } from 'hooks/useEventRoundData';
import {
  relayOverallColumns,
  relayRaceColumns,
  relayRaceGrid,
} from 'config/admin/result';
import { getRelayOverall } from 'api/resultApi';
import { clubFeaturesOf, clubForId } from 'utils/clubFeatures';
import { teamWordsOf } from 'utils/teamLabel';
import {
  OVERALL_TAB,
  isRelayEvent,
  overallTableRows,
  roundTabsWithOverall,
} from 'utils/relayTeams';

const EventViewTrackResults = ({
  clubId,
  meetId,
  eventType,
  eventID,
  isOpenEditPopup,
  isOpenEditEventPopup,
}) => {
  const navigate = useNavigate();
  const userPermissionLevel = getUserPermissionLevel();

  const { data, loading, error, refetch, updateResult } = useEventRoundData(
    { clubID: clubId, meetID: meetId, eventID },
    false
  );

  const [showEditPopup, setShowEditPopup] = useState(isOpenEditPopup);
  const [showEditEventPopup, setShowEditEventPopup] = useState(
    isOpenEditEventPopup
  );
  const [meet, setMeet] = useState(null);
  const [fetchingMeet, setFetchingMeet] = useState(true);
  const [clubRecords, setClubRecords] = useState([]);

  const metadata = data?.metadata;

  // A relay is run by teams (utils/relayTeams.js): its Event View shows Lane,
  // the team, Position, Result and Status, the team headed in the school's word
  // (House, Faction or Team) or Team in a club. The club comes from the club
  // list in memory.
  const isRelay = isRelayEvent(eventType);
  const club = useSelector((state) => clubForId(state.user, clubId));
  const teamWord = clubFeaturesOf(club).teams ? teamWordsOf(club).team : 'Team';
  const relayColumns = useMemo(
    () => (isRelay ? relayRaceColumns({ teamWord }) : null),
    [isRelay, teamWord]
  );

  // Every relay round has an Overall tab: every team of the round's races by
  // its aggregate position, asked for when the tab is chosen (one request,
  // served from the server's saved copy).
  const [overallTab, setOverallTab] = useState(null);
  const [overallRows, setOverallRows] = useState([]);
  const [fetchingOverall, setFetchingOverall] = useState(false);
  useEffect(() => setOverallTab(null), [eventID]);

  const tableRows = useMemo(
    () => (data?.results ? resultsToEventTrackTableData(data.results) : []),
    [data?.results]
  );

  const dataTypeOptions = roundTabsWithOverall(
    metadata?.siblingRounds,
    isRelay
  );

  const fetchMeet = useCallback(async () => {
    try {
      const response = await getMeetSummary(clubId, meetId);
      setMeet(response.data);
    } catch (err) {
      console.log('[Results] Fetch Meets error: ', err);
    }
  }, [clubId, meetId]);

  // Stable string of distinct athlete ages — only changes when the actual
  // age set differs (rare across sibling rounds of the same event), so
  // fetchClubRecords doesn't refire on every tab switch.
  const distinctAgesQuery = useMemo(() => {
    if (!tableRows.length) return '';
    const ages = [
      ...new Set(
        tableRows
          .map((r) => r.athleteAge)
          .filter((age) => age && age > 0)
          .map(String)
      ),
    ].sort();
    return ages.join(', ');
  }, [tableRows]);

  const fetchClubRecords = useCallback(async () => {
    if (!clubId || !eventType || !metadata?.gender || !distinctAgesQuery) {
      return;
    }
    const response = await getClubRecordsWithAgeGroups(
      clubId,
      eventType,
      distinctAgesQuery,
      metadata.gender
    ).catch((e) => {
      console.error(e);
      return null;
    });
    if (response) {
      setClubRecords(response.data);
    }
  }, [clubId, eventType, metadata?.gender, distinctAgesQuery]);

  useEffect(() => {
    fetchMeet().then(() => setFetchingMeet(false));
  }, [fetchMeet]);

  useEffect(() => {
    fetchClubRecords();
  }, [fetchClubRecords]);

  const handleRoundOptionChange = async (name, value) => {
    if (String(value).startsWith(OVERALL_TAB)) {
      const tab = dataTypeOptions.find((option) => option.value === value);
      if (!tab) return;
      setOverallTab(tab);
      setFetchingOverall(true);
      try {
        const rows = await getRelayOverall({
          clubID: clubId,
          meetID: meetId,
          eventID: tab.overallOf,
        });
        setOverallRows(overallTableRows(rows));
      } catch (err) {
        console.error('[EventViewTrackResults] Fetch overall error: ', err);
        setOverallRows([]);
      } finally {
        setFetchingOverall(false);
      }
      return;
    }
    setOverallTab(null);
    const sibling = metadata?.siblingRounds?.find((r) => r.eventID === value);
    if (!sibling) return;
    navigate(
      routes.admin.eventRound.url(clubId, meetId, eventType, sibling.eventID)
    );
  };

  const openEditPopup = () => setShowEditPopup(true);
  const closeEditPopup = () => setShowEditPopup(false);
  const openEditEventPopup = () => setShowEditEventPopup(true);
  const closeEditEventPopup = () => setShowEditEventPopup(false);

  // After results are saved or deleted in the dialog: fetch this event again
  // (one request, no page reload). Returns a promise so the dialog can wait
  // for the saved results before it lets another change be saved.
  const refreshResults = () => refetch();

  // After a relay team's runners are saved: the row takes the runners the
  // server answered, with no request (step 3 file, section 13).
  const handleRunnersSaved = (resultID, runners) =>
    updateResult(resultID, { runners });

  return (
    <>
      <MetaTags title="SplitFast | Results" />
      <AdminTablePageLayout
        loading={fetchingMeet || (loading && !data) || fetchingOverall}
      >
        <EventResultPageHeader
          title={
            metadata
              ? `${metadata.eventType} ${metadata.eventAgeGroup} ${metadata.gender}`
              : 'Results'
          }
          suffix={
            metadata?.publishingStatus === 'Preliminary' ? 'Preliminary' : ''
          }
          viewLabel="Round Results"
          clubRecords={clubRecords}
          eventType={eventType}
          meet={meet}
          clubId={clubId}
        />

        <EventResultPageOptionButton
          handleEditResult={
            userPermissionLevel >= UserPermissionLevel.Official
              ? openEditPopup
              : undefined
          }
          handleEditEvent={
            userPermissionLevel >= UserPermissionLevel.Official
              ? openEditEventPopup
              : undefined
          }
          isAdd={tableRows.length === 0}
          // The Overall tab lists several races; results are edited in a race.
          showEditResultButton={
            !overallTab && userPermissionLevel >= UserPermissionLevel.Official
          }
          showEditEventButton={
            !overallTab && userPermissionLevel >= UserPermissionLevel.Official
          }
        />

        {error ? (
          <div style={{ textAlign: 'center' }}>
            {error?.response?.data?.error || 'Event not found'}
          </div>
        ) : tableRows.length || metadata ? (
          // The event is known (metadata) even when it has no results yet:
          // show its round tabs and an empty table, ready for results.
          overallTab ? (
            <ResultDataTable
              dataTypeOptions={dataTypeOptions}
              optionValues={{ dataType: overallTab.value }}
              handleOptionChange={handleRoundOptionChange}
              results={overallRows}
              eventType={eventType}
              columns={relayOverallColumns({ teamWord })}
              gridTemplateColumns={relayRaceGrid(
                relayOverallColumns({ teamWord })
              )}
              showEmptyTable
              emptyMessage="No team has a result in this round yet."
            />
          ) : (
            <ResultDataTable
              dataTypeOptions={dataTypeOptions}
              optionValues={{ dataType: eventID }}
              handleOptionChange={handleRoundOptionChange}
              results={tableRows}
              eventType={eventType}
              columns={relayColumns}
              gridTemplateColumns={
                relayColumns ? relayRaceGrid(relayColumns) : null
              }
              showEmptyTable
              emptyMessage={
                userPermissionLevel >= UserPermissionLevel.Official
                  ? 'No results yet. Use + Add Results to enter them.'
                  : 'No results yet.'
              }
            />
          )
        ) : (
          <div style={{ textAlign: 'center' }}>
            {loading ? 'Loading results...' : 'No results found'}
          </div>
        )}
      </AdminTablePageLayout>
      {userPermissionLevel >= UserPermissionLevel.Official && (
        <ResultEditPopup
          results={tableRows}
          clubID={clubId}
          meetID={meetId}
          eventID={eventID}
          viewType="event-view"
          eventType={eventType}
          ageGroup={metadata?.eventAgeGroup}
          gender={metadata?.gender}
          roundType={metadata?.roundType}
          preliminary={metadata?.publishingStatus === 'Preliminary'}
          title={
            metadata
              ? `Results for ${metadata.eventType} ${metadata.eventAgeGroup} ${metadata.gender}`
              : 'Results'
          }
          subtitle={meet ? convertDateTime(meet?.meetDate || '').date : ''}
          showPopup={showEditPopup}
          closePopup={closeEditPopup}
          refreshResults={refreshResults}
          onRunnersSaved={handleRunnersSaved}
        />
      )}
      {userPermissionLevel >= UserPermissionLevel.Official && (
        <EventPopup
          mode="edit"
          eventID={eventID}
          clubID={clubId}
          meetID={meetId}
          showPopup={showEditEventPopup}
          closePopup={closeEditEventPopup}
          onSuccess={refreshResults}
        />
      )}
    </>
  );
};

EventViewTrackResults.propTypes = {
  clubId: PropTypes.string,
  meetId: PropTypes.string,
  eventType: PropTypes.string,
  eventID: PropTypes.string,
  isOpenEditPopup: PropTypes.bool,
  isOpenEditEventPopup: PropTypes.bool,
};

export default EventViewTrackResults;
